import mixpanel from 'mixpanel-browser';
import { useEffectOnce } from 'react-use';

const {
  REACT_APP_ENV = 'development',
  REACT_APP_MIXPANEL_TOKEN = '',
  REACT_APP_MIXPANEL_PROXY_URL = '',
} = process.env;

const IS_DEV = REACT_APP_ENV === 'development';

// Without a real token mixpanel.init leaves the client half-built, and the
// first track() then dies reading config.disable_all_events, which takes the
// whole app down. Analytics is optional; the app is not.
const HAS_TOKEN =
  REACT_APP_MIXPANEL_TOKEN.length > 0 && REACT_APP_MIXPANEL_TOKEN !== 'dummy';
const HAS_PROXY = /^https?:\/\//.test(REACT_APP_MIXPANEL_PROXY_URL);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MIXPANEL_CUSTOM_LIB_URL = REACT_APP_MIXPANEL_PROXY_URL + '/lib.min.js';

function startAnalytics(): boolean {
  if (IS_DEV || !HAS_TOKEN) return false;
  try {
    mixpanel.init(REACT_APP_MIXPANEL_TOKEN, {
      track_pageview: 'url-with-path-and-query-string',
      // Only override the host when one was actually supplied, otherwise the
      // client points at an unresolvable host and every call fails.
      ...(HAS_PROXY ? { api_host: REACT_APP_MIXPANEL_PROXY_URL } : {}),
    });
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Analytics disabled: Mixpanel failed to start', error);
    return false;
  }
}

const shouldEnableAnalytics = startAnalytics();

if (!shouldEnableAnalytics && !IS_DEV) {
  // eslint-disable-next-line no-console
  console.warn(
    `Analytics disabled in ${REACT_APP_ENV}: no valid Mixpanel token`
  );
}

// Analytics must never be able to throw into React's render path.
function safely(action: () => void) {
  try {
    action();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Analytics call failed', error);
  }
}

export function setUser({
  id,
  email,
  name,
  isSignUp,
}: {
  id: number;
  email: string;
  name: string;
  isSignUp: boolean;
}) {
  if (shouldEnableAnalytics) {
    safely(() => {
      mixpanel.people.set({ email, name, Email: email, $email: email });
      if (isSignUp) {
        mixpanel.alias(id.toString());
      } else {
        mixpanel.identify(id.toString());
      }
    });
  } else {
    console.log(
      `Skipping Analytics Mixpanel setUser as ENV = ${REACT_APP_ENV}`
    );
  }
}

export function trackPageView() {
  trackEvent({
    event: '$mp_web_page_view',
  });
}

export function setUserProperties(properties: Record<string, unknown>) {
  if (shouldEnableAnalytics) {
    safely(() => mixpanel.people.set(properties));
  } else {
    console.log(
      `Skipping Analytics Mixpanel setUserProperties as ENV = ${REACT_APP_ENV}`,
      properties
    );
  }
}

export function removeUser() {
  if (shouldEnableAnalytics) {
    safely(() => mixpanel.reset());
  } else {
    console.log(
      `Skipping Analytics Mixpanel removeUser as ENV = ${REACT_APP_ENV}`
    );
  }
}

export function trackEvent({
  event,
  properties = {},
}: {
  event: string;
  properties?: Record<string, unknown>;
}) {
  if (shouldEnableAnalytics) {
    safely(() => mixpanel.track(event, properties));
  } else {
    console.log(
      `Skipping Analytics Mixpanel trackEvent for ${event} as ENV = ${REACT_APP_ENV}`,
      properties
    );
  }
}

export const useTrackViewOnMount = (
  event: string,
  properties: Record<string, unknown> = {}
) => {
  // Parse utm params
  const currentUrl = new URL(window.location.href);

  const utm_keys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  const newProps = utm_keys
    .map((key) => {
      const value = currentUrl.searchParams.get(key);
      return { key, value };
    })
    .filter((prop) => prop.value);

  const fullProperties = {
    ...properties,
    ...Object.fromEntries(newProps.map((prop) => [prop.key, prop.value])),
  };

  useEffectOnce(() => {
    trackEvent({
      event,
      properties: fullProperties || {},
    });
  });
};

export const EVENTS = {
  ACTIVITY: {
    QUESTION: {
      TYPE_ANSWER: 'activity_question_type_answer',
    },
  },
  COMMON: {
    VIEW_HOME: 'view_home',
    AUTHENTHICATE: 'authenticate',
    RUN_EMBEDDED_PLAYGROUND: 'run_embedded_playground',
  },
  PROSUMER: {
    // tracking this whenever a successfull profile request is made. will use it to get the unique prosumer count
    // we use the profile on every prosumer-related component
    PING: 'prosumer_ping',
    ONBOARDING: {
      VIEW_ASK_TOPIC: 'prosumer_onboarding_view_ask_topic',
      VIEW_ASK_PROFILE: 'prosumer_onboarding_view_ask_profile',
      VIEW_ASK_ROLE: 'prosumer_onboarding_view_ask_role',
      VIEW_POST_ASK_ROLE: 'prosumer_onboarding_view_post_ask_role',
      VIEW_ASK_APPLICATIONS: 'prosumer_onboarding_view_ask_applications',
      VIEW_ASK_SECONDARY_TOPICS:
        'prosumer_onboarding_view_ask_secondary_topics',
      VIEW_ASK_GOAL: 'prosumer_onboarding_view_ask_goal',
      VIEW_POST_ASK_GOAL: 'prosumer_onboarding_view_post_ask_goal',
      VIEW_ASK_TOPIC_KNOWLEDGE: 'prosumer_onboarding_view_ask_topic_knowledge',
      VIEW_ASK_THEORY: 'prosumer_onboarding_view_ask_theory',
      VIEW_ASK_TIME_EFFORT: 'prosumer_onboarding_view_ask_time_effort',
      VIEW_PERRSONALIZATION_LOADER:
        'prosumer_onboarding_view_personalization_loader',
      VIEW_ASK_EMAIL: 'prosumer_onboarding_view_ask_email',
      VIEW_RESULTS: 'prosumer_onboarding_view_results',
      VIEW_PERSONALIZED_PLAN: 'prosumer_onboarding_view_personalized_plan',
      VIEW_UPSELL_BENEFITS: 'prosumer_onboarding_view_upsell_benefits',
      VIEW_UPSELL_PAYMENT: 'prosumer_onboarding_view_payment',
      VIEW_UPSELL_PAYMENT_CALLBACK: 'prosumer_onboarding_view_payment_callback',
      VIEW_PARTNER_WELCOME: 'prosumer_onboarding_view_partner_welcome',
      VIEW_SIGNUP: 'prosumer_onboarding_view_signup',
      VIEW_SIGNUP_CONFIRM_EMAIL:
        'prosumer_onboarding_view_signup_confirm_email',
      VIEW_SIGNUP_CALLBACK: 'prosumer_onboarding_view_signup_callback',
      VIEW_ERROR: 'prosumer_onboarding_view_error',
    },
    UPSELL: {
      VIEW_BENEFITS: 'prosumer_upsell_view_benefits',
      VIEW_PAYMENT: 'prosumer_upsell_view_payment',
      VIEW_PAYMENT_CALLBACK: 'prosumer_upsell_view_payment_callback',
      VIEW_LEARNING_PLAN: 'prosumer_upsell_view_learning_plan',
    },
    ACTIVITY: {
      VIEW: 'prosumer_view_activity',
    },
    DASHBOARD: {
      VIEW: 'prosumer_view_dashboard',
    },
  },
  AI: {
    MESSAGE: {
      SEND: 'ai_message_send',
      SEND_LOCKED: 'ai_message_send_locked',
    },
  },
};
